/**
 * The environment an isolated Agent IDE deploy needs before it is allowed to do anything.
 *
 * One entry per variable, in the order the status page lists them. `secret` entries are
 * minted per account by the deploy command and mounted from Secret Manager under
 * `secretNameFor(account, name)`; the rest are plain values the manifest carries.
 *
 * This is deliberately NOT lib/env.ts's list. That schema requires 66 variables at import
 * time, most of them credentials to systems an account deploy must never hold (see
 * docs/architecture/agent-ide-account-deploy.md). Until lib/env.ts learns an account-deploy
 * profile, the preflight in front of each process is what keeps a deploy with none of these
 * from crashing at import.
 */
export interface RequiredEnvEntry {
  name: string;
  secret: boolean;
  /** What the deploy cannot do without it, in the words the status page shows. */
  purpose: string;
}

export const ACCOUNT_DEPLOY_REQUIRED_ENV: readonly RequiredEnvEntry[] = [
  { name: "AGENT_IDE_ACCOUNT", secret: false, purpose: "the account this deploy belongs to" },
  {
    name: "ENVIRONMENT",
    secret: false,
    purpose: "which environment (stg or prd) this deploy runs in",
  },
  { name: "DATABASE_URL", secret: true, purpose: "this account's database (primary)" },
  {
    name: "DATABASE_READ_REPLICA_URL",
    secret: true,
    purpose: "this account's database (read replica)",
  },
  {
    name: "TEMPORAL_ADDRESS",
    secret: true,
    purpose: "the Temporal server its background work runs on",
  },
  { name: "TEMPORAL_NAMESPACE", secret: true, purpose: "this account's own Temporal namespace" },
  { name: "TEMPORAL_API_KEY", secret: true, purpose: "the key for that namespace" },
  { name: "ANTHROPIC_API_KEY", secret: true, purpose: "the account-scoped key Sherpa runs on" },
  { name: "NETIC_JWT_SECRET", secret: true, purpose: "signing the IDE's session cookie" },
  { name: "FIREBASE_PROJECT_ID", secret: true, purpose: "verifying sign-in (Firebase Admin)" },
  { name: "FIREBASE_CLIENT_EMAIL", secret: true, purpose: "verifying sign-in (Firebase Admin)" },
  { name: "FIREBASE_PRIVATE_KEY", secret: true, purpose: "verifying sign-in (Firebase Admin)" },
];

/** The Secret Manager secret an account deploy's variable is mounted from. */
export function secretNameFor(account: string, name: string): string {
  return `agent-ide-${account}-${name}`;
}
